import type { DsBin } from './BinPill';

/**
 * CWY BinGlyph — the mastery bin as a SHAPE, not only a colour.
 *
 * The design system is deliberately icon-light and leans on the bin dots as its
 * iconography, but a plain dot encodes the bin in hue alone. Under the common
 * red-green deficiencies Green (#639922) and Orange (#BA7517) converge, and
 * Pink sits close behind — so for roughly one student in twelve the product's
 * core visual language carried no information at all. That is a WCAG 1.4.1
 * failure and, more to the point, it is the one signal the whole app is built
 * on.
 *
 * Each bin now also has a distinct silhouette, readable at 10px and in
 * greyscale (a printed PTM report is greyscale). Colour still does the fast
 * work for everyone who can see it; the shape is what makes it decodable when
 * colour cannot.
 */
const SHAPE: Record<DsBin, { path: string; filled: boolean; label: string }> = {
  // Mastered first try — a solid disc, the "complete" end of the ramp.
  green: { path: 'M5 0.6a4.4 4.4 0 1 1 0 8.8 4.4 4.4 0 0 1 0-8.8z', filled: true, label: 'Green' },
  // Mastered, but slowly — a ring: complete, with something still open.
  purple: {
    path: 'M5 0.6a4.4 4.4 0 1 1 0 8.8 4.4 4.4 0 0 1 0-8.8zm0 2.2a2.2 2.2 0 1 0 0 4.4 2.2 2.2 0 0 0 0-4.4z',
    filled: true,
    label: 'Purple',
  },
  // Second attempt — a square.
  blue: { path: 'M1 1h8v8H1z', filled: true, label: 'Blue' },
  // Third attempt — a triangle, the "warning" silhouette.
  orange: { path: 'M5 0.7 9.6 9.3H0.4z', filled: true, label: 'Orange' },
  // Unsolved — a hollow diamond: the only outline shape, so "nothing yet" reads
  // as empty rather than as just another colour.
  pink: { path: 'M5 0.8 9.2 5 5 9.2 0.8 5z', filled: false, label: 'Pink' },
};

export interface BinGlyphProps {
  bin: DsBin;
  size?: number;
  /** Colour of the shape. Defaults to the bin's border tone. */
  color?: string;
  /** Screen-reader text. Pass null when an adjacent label already names the bin. */
  title?: string | null;
}

export function BinGlyph({ bin, size = 10, color, title }: BinGlyphProps) {
  const shape = SHAPE[bin];
  const tone = color ?? `var(--${bin}-border)`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 10 10"
      style={{ flex: 'none', display: 'block' }}
      role={title ? 'img' : undefined}
      aria-label={title ?? undefined}
      aria-hidden={title ? undefined : true}
    >
      <path
        d={shape.path}
        fill={shape.filled ? tone : 'none'}
        stroke={tone}
        strokeWidth={shape.filled ? 0 : 1.6}
        strokeLinejoin="round"
        fillRule="evenodd"
      />
    </svg>
  );
}

export { SHAPE as BIN_SHAPE };
